<template>
  <div class="bg-white min-h-screen font-sans antialiased text-gray-900">
    <div class="container mx-auto px-6 py-12 max-w-5xl">
      <div class="bg-gradient-to-r from-[#1583d4] to-[#0f5fa0] text-white rounded-2xl shadow-xl p-10 mb-10 text-center">
        <h1 class="text-4xl font-extrabold mb-2 tracking-tight">
          {{ enterprise.name }}
        </h1>
        <h2 class="text-xl md:text-2xl font-semibold opacity-90 mb-3">
          Cadastre seu Imóvel
        </h2>
        <p class="text-[#d0e7fa] text-base">
          Preencha os dados abaixo para anunciar seu imóvel de forma rápida,
          moderna e segura.
        </p>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-12">
        <div class="section-container animate-fade-in">
          <h2 class="section-title">
            <span class="icon-circle">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#1583d4]" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" />
              </svg>
            </span>
            Detalhes do Imóvel
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="field-group">
              <label class="label label-required">Operação</label>
              <n-select v-model:value="form.operation" :options="options.operation" placeholder="Selecione" size="large"
                class="input-focused" :class="{ 'border-red-500': errors.operation }" />
              <p v-if="errors.operation" class="error">{{ errors.operation }}</p>
            </div>

            <div class="field-group">
              <label class="label label-required">Tipo de Imóvel</label>
              <n-select v-model:value="form.type" :options="options.types" placeholder="Selecione" size="large"
                class="input-focused" :class="{ 'border-red-500': errors.type }" />
              <p v-if="errors.type" class="error">{{ errors.type }}</p>
            </div>

            <div class="col-span-1 md:col-span-2 field-group">
              <label class="label label-required">Título do Anúncio</label>
              <n-input v-model:value="form.title" placeholder="Ex: Casa espaçosa com 3 quartos" size="large"
                class="input-focused" :class="{ 'border-red-500': errors.title }" />
              <p v-if="errors.title" class="error">{{ errors.title }}</p>
            </div>

            <div class="col-span-1 md:col-span-2 field-group">
              <label class="label label-required">Descrição</label>
              <n-input v-model:value="form.description" type="textarea" placeholder="Descreva seu imóvel em detalhes"
                style="min-height: 200px" class="input-focused resize-none"
                :class="{ 'border-red-500': errors.description }" />
              <p v-if="errors.description" class="error">
                {{ errors.description }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="form.type === 'land'" class="section-container animate-fade-in">
          <h2 class="section-title">
            <span class="icon-circle">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#1583d4]" viewBox="0 0 24 24" fill="none"
                stroke="currentColor">
                <path d="M12 22s8-4 8-10V5L12 2 4 5v7c0 6 8 10 8 10z" />
              </svg>
            </span>
            Detalhes do Terreno
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="field-group">
              <label class="label">Nome do Loteamento</label>
              <n-input v-model:value="form.subdivision_name" placeholder="Ex: Loteamento Vista Verde" size="large"
                class="input-focused" />
            </div>
            <div class="field-group">
              <label class="label">Data de Entrega</label>
              <n-date-picker v-model:value="form.delivery_date" type="date" size="large" class="input-focused"
                placeholder="dd/mm/aaaa" :class="{ 'border-red-500': errors.delivery_date }" />
            </div>
            <div class="col-span-1 md:col-span-2 field-group">
              <label class="label">Pronto para Construir</label>
              <div class="flex items-center space-x-4 mt-2">
                <n-switch v-model:value="form.ready_to_build" size="large" />
                <span class="ml-2 text-sm text-gray-600">
                  {{ form.ready_to_build ? "Sim" : "Não" }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="section-container animate-fade-in">
          <h2 class="section-title">
            <span class="icon-circle">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#1583d4]" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              </svg>
            </span>
            Localização e Valores
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="field-group">
              <label class="label label-required">CEP</label>
              <n-input v-model:value="form.zip_code" v-maska="'#####-###'" placeholder="Ex: 14000-000" size="large"
                class="input-focused" @blur="consultaCEP" :loading="isCepLoading"
                :class="{ 'border-red-500': errors.zip_code }" />
              <p v-if="errors.zip_code" class="error">{{ errors.zip_code }}</p>
            </div>
            <div class="field-group">
              <label class="label label-required">Estado</label>
              <n-input v-model:value="form.state" placeholder="Ex: SP" size="large" maxlength="2" class="input-focused"
                :class="{ 'border-red-500': errors.state }" />
              <p v-if="errors.state" class="error">{{ errors.state }}</p>
            </div>
            <div class="field-group">
              <label class="label label-required">Cidade</label>
              <n-input v-model:value="form.city" placeholder="Ex: São Paulo" size="large" class="input-focused"
                :class="{ 'border-red-500': errors.city }" />
              <p v-if="errors.city" class="error">{{ errors.city }}</p>
            </div>
            <div class="field-group">
              <label class="label label-required">Bairro</label>
              <n-input v-model:value="form.neighborhood" placeholder="Ex: Jardim Botânico" size="large"
                class="input-focused" :class="{ 'border-red-500': errors.neighborhood }" />
              <p v-if="errors.neighborhood" class="error">{{ errors.neighborhood }}</p>
            </div>
            <div class="field-group">
              <label class="label label-required">Logradouro</label>
              <n-input v-model:value="form.address" placeholder="Rua ou Avenida" size="large" class="input-focused"
                :class="{ 'border-red-500': errors.address }" />
              <p v-if="errors.address" class="error">{{ errors.address }}</p>
            </div>
            <div class="field-group">
              <label class="label label-required">Número</label>
              <n-input v-model:value="form.number" placeholder="Ex: 123" size="large" class="input-focused"
                :class="{ 'border-red-500': errors.number }" />
              <p v-if="errors.number" class="error">{{ errors.number }}</p>
            </div>
            <div class="field-group col-span-1 md:col-span-2">
              <label class="label">Complemento</label>
              <n-input v-model:value="form.complement" placeholder="Ex: Apto 101, Bloco A" size="large"
                class="input-focused" />
            </div>

            <div class="col-span-1 md:col-span-2 field-group">
              <label class="label label-required price-label-highlight">Preço</label>
              <n-input v-model:value="form.price" size="large" @input="formatCurrencyInput('price', $event)"
                placeholder="R$ 500.000,00" class="input-focused price-input-highlight"
                :class="{ 'border-red-500': errors.price }" />
              <p v-if="errors.price" class="error">{{ errors.price }}</p>
            </div>

            <div class="field-group">
              <label class="label">Valor do IPTU</label>
              <n-input v-model:value="form.iptu_value" size="large" @input="formatCurrencyInput('iptu_value', $event)"
                placeholder="R$ 1.500,00" class="input-focused" />
            </div>
            <div class="field-group">
              <label class="label">Periodicidade do IPTU</label>
              <n-select v-model:value="form.iptu_periodicity" :options="options.iptuPeriodicity" placeholder="Selecione"
                size="large" class="input-focused" />
            </div>
          </div>
        </div>

        <div class="section-container animate-fade-in">
          <h2 class="section-title">
            <span class="icon-circle">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#1583d4]" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
            </span>
            Características
          </h2>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div class="field-group">
              <label class="label">Área Útil (m²)</label>
              <n-input-number v-model:value="form.usable_area" :min="0" size="large" placeholder="Ex: 120"
                class="input-focused" />
            </div>
            <div class="field-group">
              <label class="label">Área Construída (m²)</label>
              <n-input-number v-model:value="form.built_area" :min="0" size="large" placeholder="Ex: 150"
                class="input-focused" />
            </div>
            <div class="field-group">
              <label class="label">Área Total (m²)</label>
              <n-input-number v-model:value="form.total_area" :min="0" size="large" placeholder="Ex: 200"
                class="input-focused" />
            </div>
            <div class="field-group">
              <label class="label">Área do Lote (m²)</label>
              <n-input-number v-model:value="form.lot_area" :min="0" size="large" placeholder="Ex: 300"
                class="input-focused" />
            </div>
            <div v-if="form.type !== 'land'" class="field-group">
              <label class="label">Dormitórios</label>
              <n-input-number v-model:value="form.bedrooms" :min="0" size="large" placeholder="Ex: 1"
                class="w-full input-focused" />
              <p v-if="errors.bedrooms" class="error">{{ errors.bedrooms }}</p>
            </div>
            <div v-if="form.type !== 'land'" class="field-group">
              <label class="label">Suítes</label>
              <n-input-number v-model:value="form.suites" :min="0" size="large" placeholder="Ex: 1"
                class="w-full input-focused" />
            </div>
            <div v-if="form.type !== 'land'" class="field-group">
              <label class="label">Banheiros</label>
              <n-input-number v-model:value="form.bathrooms" :min="0" size="large" placeholder="Ex: 2"
                class="w-full input-focused" />
            </div>
            <div v-if="form.type !== 'land'" class="field-group">
              <label class="label">Vagas de Garagem</label>
              <n-input-number v-model:value="form.garage" :min="0" size="large" placeholder="Ex: 1"
                class="w-full input-focused" />
            </div>
            <div class="field-group col-span-full">
              <label class="label">Características do Imóvel</label>
              <n-select v-model:value="form.features" :options="featureOptions" label-field="label" value-field="value"
                placeholder="Selecione uma ou mais características" multiple filterable />
            </div>
            <div class="field-group col-span-full">
              <label class="label">Destaques</label>
              <n-input v-model:value="form.highlights" type="textarea" placeholder="Destaque pontos importantes"
                style="min-height: 100px" class="input-focused resize-none" />
            </div>
            <div class="field-group col-span-full">
              <label class="label">Ponto de Referência</label>
              <n-input v-model:value="form.reference_point" type="textarea" placeholder="Ex: Próximo à praça central"
                style="min-height: 100px" class="input-focused resize-none" />
            </div>
          </div>
        </div>

        <div class="section-container animate-fade-in">
          <h2 class="section-title">
            <span class="icon-circle">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6"
                stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-[#1583d4]">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </span>
            Fotos
          </h2>
          <div class="space-y-6">
            <div class="field-group">
              <label class="label label-required">Foto Principal</label>
              <p class="text-xs text-gray-500 mb-2">
                Essa será a foto de capa do seu anúncio.
              </p>
              <n-upload list-type="image-card" :max="1" v-model:file-list="mainImage" @before-upload="beforeUpload"
                class="upload-box main-photo" />
              <p v-if="errors.mainImage" class="error">{{ errors.mainImage }}</p>
            </div>
            <div class="field-group">
              <label class="label">Fotos Adicionais</label>
              <p class="text-xs text-gray-500 mb-2">
                Adicione o mínimo de 3 fotos extras do seu imóvel.
              </p>
              <n-upload multiple list-type="image-card" :max="19" v-model:file-list="additionalImages"
                @before-upload="beforeUpload" class="upload-box grid-photos" />
              <p v-if="errors.images" class="error">{{ errors.images }}</p>
            </div>
          </div>
        </div>

        <div class="section-container animate-fade-in">
          <h2 class="section-title">
            <span class="icon-circle">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6"
                stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-[#1583d4]">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </span>
            Dados de Proprietário
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mt-6">
            <div class="field-group">
              <label class="label label-required">Nome</label>
              <n-input v-model:value="form.contactName" placeholder="Nome completo" size="large" class="input-focused"
                :class="{ 'border-red-500': errors.contactName }" />
              <p v-if="errors.contactName" class="error">{{ errors.contactName }}</p>
            </div>
            <div class="field-group">
              <label class="label label-required">Telefone</label>
              <n-input v-model:value="form.contactPhone" v-maska="'(##) #####-####'" placeholder="(00) 00000-0000"
                size="large" class="input-focused" :class="{ 'border-red-500': errors.contactPhone }" />
              <p v-if="errors.contactPhone" class="error">{{ errors.contactPhone }}</p>
            </div>
            <div class="field-group">
              <label class="label label-required">E-mail</label>
              <n-input v-model:value="form.contactEmail" placeholder="email@email.com.br" size="large"
                class="input-focused" :class="{ 'border-red-500': errors.contactEmail }" />
              <p v-if="errors.contactEmail" class="error">{{ errors.contactEmail }}</p>
            </div>
          </div>
        </div>

        <div class="section-container animate-fade-in bg-red-50 p-6 border border-red-200 rounded-xl shadow-md">
          <h2 class="text-2xl font-bold text-red-800 mb-4 flex items-center">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none"
              stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 mr-3 text-red-600">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            Declaração de Responsabilidade
          </h2>

          <div class="text-sm text-gray-700 space-y-4 mb-6 leading-relaxed">
            <p>Ao submeter este formulário, eu, o proprietário do imóvel, **{{ form.contactName }}**, **declaro sob pena de lei**:</p>
            <ul class="list-disc list-inside ml-4 space-y-1">
              <li>Que sou o legítimo proprietário(a) ou representante legal com **plena autoridade** para dispor e
                divulgar este imóvel.</li>
              <li>Que **aceito integralmente os termos de divulgação** e autorizo a utilização das fotos, vídeos e dados
                fornecidos pela imobiliária.</li>
              <li>Que sou o **único e exclusivo responsável pela veracidade, precisão e licitude** de todas as
                informações (incluindo preço, áreas, localização) e imagens inseridas no sistema.</li>
              <li>E que **isento a imobiliária** de qualquer responsabilidade civil, criminal, ou por danos a terceiros
                decorrentes de violação de direitos autorais ou informações falsas.</li>
            </ul>
            <p class="font-bold text-red-700">A submissão do cadastro está condicionada ao aceite desta declaração.</p>
          </div>

          <div class="field-group">
            <label class="flex items-start cursor-pointer">
              <input type="checkbox" v-model="form.terms_accepted"
                class="mt-1 mr-3 h-5 w-5 text-[#1583d4] border-gray-300 rounded focus:ring-[#1583d4]"
                :class="{ 'border-red-500 ring-2 ring-red-500': errors.terms_accepted }">
              <span class="text-base font-medium text-gray-900 select-none">
                Li e **Aceito** a Declaração de Responsabilidade e Termos de Divulgação acima.
              </span>
            </label>
            <p v-if="errors.terms_accepted" class="error mt-2">
              {{ errors.terms_accepted }}
            </p>
          </div>
        </div>

        <div class="flex justify-center pt-8">
          <n-button type="primary" size="large" attr-type="submit" :loading="isSubmitting" class="btn-submit"
            :disabled="!form.terms_accepted">
            {{ isSubmitting ? 'Enviando...' : 'Salvar Cadastro' }}
          </n-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import {
  NSelect,
  NInput,
  NInputNumber,
  NUpload,
  NButton,
  useMessage,
  NSwitch,
  NDatePicker,
} from "naive-ui";
import { vMaska } from "maska/vue";
import api from "@/services/api";
import { useLoadingStore } from "@/store/loadingStore";

const message = useMessage();
const errors = ref({});
const mainImage = ref([]);
const additionalImages = ref([]);
const isSubmitting = ref(false);

const isCepLoading = ref(false);
const isAddressDisabled = ref(false);
const enterprise = ref({});

const loadingStore = useLoadingStore();

const createInitialFormState = () => ({
  title: null,
  code: null,
  slug: null,
  description: null,
  operation: null,
  type: null,
  year: null,
  price: null,
  bedrooms: null,
  bathrooms: null,
  suites: null,
  garage: null,
  area: null,
  address: null,
  number: null,
  complement: null,
  neighborhood: null,
  city: null,
  state: null,
  zip_code: null,
  responsible_broker: null,
  responsible_broker_phone: null,
  for_rent: false,
  for_sale: false,
  is_launch: false,
  is_reserved: false,
  is_active: true,
  images: null,
  status: 'in_analysis',
  iptu_value: null,
  iptu_periodicity: null,
  total_area: null,
  usable_area: null,
  built_area: null,
  lot_area: null,
  lot_front: null,
  lot_back: null,
  features: null,
  highlights: null,
  reference_point: null,
  subdivision_name: null,
  delivery_date: null,
  ready_to_build: false,
  contactName: null,
  contactPhone: null,
  contactEmail: null,
  terms_accepted: false,
});

const form = ref(createInitialFormState());

const featureOptions = ref([
  { label: 'Aceita pet', value: 'pet_friendly' },
  { label: 'Armários', value: 'closets' },
  { label: 'Local tranquilo', value: 'calm_location' },
  { label: 'Piso Frio', value: 'cold_floor' },
  { label: 'Quintal', value: 'backyard' },
  { label: 'Ventilador', value: 'fan' },
  { label: 'Área de Serviço', value: 'service_area' },
  { label: 'Cozinha', value: 'kitchen' },
  { label: 'Escola Próxima', value: 'near_school' },
  { label: 'Farmácia Próxima', value: 'near_pharmacy' },
  { label: 'Piscina', value: 'pool' },
  { label: 'Churrasqueira', value: 'grill' },
  { label: 'Quadra de Futebol', value: 'football_court' },
  { label: 'Quadra de Vôlei', value: 'volleyball_court' },
  { label: 'Quadra de Tênis', value: 'tennis_court' },
  { label: 'Quadra de Esporte', value: 'sports_court' },
]);

const options = {
  operation: [
    { label: "Venda", value: "sale" },
    { label: "Aluguel", value: "rent" },
    { label: "Ambos", value: "both" },
  ],
  types: [
    { label: "Casa", value: "house" },
    { label: "Apartamento", value: "apartment" },
    { label: "Terreno", value: "land" },
    { label: "Comercial", value: "commercial" },
    { label: "Rural", value: "rural" },
  ],
  iptuPeriodicity: [
    { label: "Mensal", value: "monthly" },
    { label: "Anual", value: "annually" },
    { label: "Pagamento Único", value: "single_payment" },
  ],
};

watch(() => form.value.zip_code, (newZipCode) => {
  if (isAddressDisabled.value) {
    form.value.state = null;
    form.value.address = null;
    form.value.number = null;
    form.value.city = null;
    form.value.neighborhood = null;
  }
  if (!newZipCode) {
    isAddressDisabled.value = false;
  }
});

watch(
  () => form.value.type,
  (newType) => {
    // Resetar campos de terreno quando o tipo de imóvel mudar
    if (newType !== "land") {
      form.value.subdivision_name = null;
      form.value.delivery_date = null;
      form.value.ready_to_build = false;
    }
  }
);

async function consultaCEP() {
  const cep = form.value.zip_code ? form.value.zip_code.replace(/\D/g, "") : null;
  if (!cep || cep.length !== 8) {
    isAddressDisabled.value = false;
    return;
  }
  isCepLoading.value = true;
  isAddressDisabled.value = false;
  errors.value.zip_code = null;
  try {
    const response = await api.get(`/cep`, {
      params: { cep: cep },
    });
    if (response.data && !response.data.erro) {
      const data = response.data;
      form.value.zip_code = data.cep;
      form.value.state = data.uf;
      form.value.address = data.logradouro;
      form.value.city = data.localidade;
      form.value.neighborhood = data.bairro;
      isAddressDisabled.value = true;
      message.success("CEP encontrado com sucesso!", { duration: 6000 });
    } else {
      isAddressDisabled.value = false;
      message.warning("CEP não encontrado. Por favor, preencha o endereço manualmente.", {
        duration: 6000,
      });
    }
  } catch (error) {
    console.error("Erro na consulta de CEP:", error);
    isAddressDisabled.value = false;
    errors.value.zip_code = "Erro ao buscar CEP. Verifique o número e tente novamente.";
    message.error("Erro ao buscar CEP.", { duration: 6000 });
  } finally {
    isCepLoading.value = false;
  }
}

function formatCurrencyInput(field, value) {
  const clean = (value || '').toString().replace(/\D/g, "");
  if (!clean) {
    form.value[field] = null;
    return;
  }
  const num = parseInt(clean, 10) / 100;
  form.value[field] = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(num);
}

function cleanNumberValue(value) {
  if (!value) return null;
  return parseFloat(value.replace(/[^\d,]/g, "").replace(",", "."));
}

function beforeUpload({ file }) {
  if (!["image/jpeg", "image/png"].includes(file.type)) {
    message.error("Apenas arquivos JPG/PNG são permitidos.", { duration: 6000 });
    return false;
  }
  return true;
}

function validateForm() {
  errors.value = {};
  const required = [
    "operation",
    "type",
    "title",
    "description",
    "state",
    "address",
    "city",
    "neighborhood",
    "price",
    "contactName",
    "contactPhone",
    "contactEmail",
    "number",
  ];

  if (form.value.type === "land") {
    const requiredForLand = [
      "operation",
      "type",
      "title",
      "description",
      "state",
      "address",
      "city",
      "neighborhood",
      "price",
      "contactName",
      "contactPhone",
      "contactEmail",
      "number",
    ];
    requiredForLand.forEach((f) => {
      if (!form.value[f]) errors.value[f] = "Campo obrigatório.";
    });
  } else {
    required.forEach((f) => {
      if (!form.value[f]) errors.value[f] = "Campo obrigatório.";
    });
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (form.value.contactEmail && !emailRegex.test(form.value.contactEmail)) {
    errors.value.contactEmail = "Por favor, insira um e-mail válido.";
  }

  // Validação de Imagens
  if (!mainImage.value || !mainImage.value.length) {
    errors.value.mainImage = "Adicione a foto principal.";
  }
  if ((mainImage.value ? mainImage.value.length : 0) + (additionalImages.value ? additionalImages.value.length : 0) < 3) {
    errors.value.images = "Adicione no mínimo 3 fotos (principal + adicionais).";
  }

  if (!form.value.terms_accepted) {
    errors.value.terms_accepted = "Você deve aceitar a Declaração de Responsabilidade para continuar.";
  }

  // Retorna true se não houver erros
  return !Object.keys(errors.value).length;
}

async function handleSubmit() {
  if (!validateForm()) {
    message.error("Por favor, corrija os erros destacados.", { duration: 6000 });
    scrollToFirstError();
    return;
  }
  isSubmitting.value = true;
  const formData = new FormData();
  const payload = {
    ...form.value,
    price: cleanNumberValue(form.value.price) ?? 0,
    iptu_value: cleanNumberValue(form.value.iptu_value) ?? 0,
    area: form.value.area ?? 0,
    bedrooms: form.value.bedrooms ?? 0,
    bathrooms: form.value.bathrooms ?? 0,
    suites: form.value.suites ?? 0,
    garage: form.value.garage ?? 0,
    status: 'in_analysis'
  };
  for (const key in payload) {
    if (typeof payload[key] === 'boolean') {
      formData.append(key, payload[key] ? 1 : 0);
    } else if (payload[key] !== null && payload[key] !== undefined) {
      if (key === 'features') {
        formData.append(key, JSON.stringify(payload[key]));
      } else {
        formData.append(key, payload[key]);
      }
    }
  }
  const allImages = [...mainImage.value, ...additionalImages.value];
  allImages.forEach((fileItem) => {
    formData.append("images", fileItem.file);
  });

  loadingStore.show();
  try {
    const response = await api.post("/property/register", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    message.success(response.data.message, { duration: 6000 });
    resetForm();
  } catch (error) {
    console.error("Erro ao enviar o formulário:", error.response || error);
    message.error(
      error?.response?.data?.error?.message ||
      "Ocorreu um erro ao enviar o formulário. Tente novamente mais tarde.",
      { duration: 6000 }
    );
  } finally {
    loadingStore.hide();
    isSubmitting.value = false;
  }
}

function resetForm() {
  form.value = createInitialFormState();
  mainImage.value = [];
  additionalImages.value = [];
  errors.value = {};
}

function scrollToFirstError() {
  const first = document.querySelector(".border-red-500");
  if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
}

const loadEnterprise = async () => {
  try {
    const response = await api.get(`enterprise`);
    enterprise.value = response.data.data[0];
  } catch (error) {
    console.error("Erro ao buscar empresa:", error);
    toastStore.showToast({
      message: error?.response?.data?.error?.error || "Erro ao buscar empresa",
      color: "error",
    });
  }
};

onMounted(async () => {
  await loadEnterprise();
});
</script>

<style scoped>
.btn-submit:deep(.n-button--loading) {
  /* Força a cor de fundo e borda para o seu azul principal */
  @apply bg-[#1583d4] !border-[#1583d4] text-white;
}

/* Garante que o spinner (o ícone de carregamento) também seja branco */
.btn-submit:deep(.n-button__icon) {
  @apply text-white;
}

.label {
  @apply block text-sm font-medium text-gray-700 mb-1;
}

.label-required {
  @apply text-[#1583d4] font-bold;
}

.required-indicator {
  @apply inline-block h-2 w-2 rounded-full bg-red-500 ml-1;
}

.error {
  @apply text-red-500 text-xs mt-1;
}

.section-container {
  @apply bg-white rounded-xl shadow-md p-8 border border-gray-100 transition-all duration-300;
}

.section-container:hover {
  @apply shadow-lg;
}

.section-title {
  @apply text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3;
}

.icon-circle {
  @apply flex items-center justify-center w-10 h-10 rounded-full bg-[#c6e0f6] text-[#1583d4];
}

.input-focused :deep(.n-input__border),
.input-focused :deep(.n-select-single) {
  @apply border-gray-300;
}

.input-focused:focus-within :deep(.n-input__border),
.input-focused:focus-within :deep(.n-select-single) {
  @apply border-[#1583d4] ring-4 ring-[#d0e8fa] shadow-md;
}

.upload-box {
  @apply w-full;
}

.main-photo :deep(.n-upload-dragger) {
  @apply border-2 border-dashed border-[#1583d4] rounded-xl p-6 bg-[#e1f0fb];
}

.grid-photos :deep(.n-upload-file-list) {
  @apply grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-2 lg:grid-cols-6;
}

.btn-submit {
  @apply w-full md:w-auto px-12 h-14 text-lg font-semibold rounded-full bg-[#1583d4] hover:bg-[#0f65a8] transition-all shadow-md hover:shadow-xl;
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>